import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getGetAiTutorThumbsDownQueryKey,
  useGetAiTutorThumbsDownCheck,
} from '@/common/api/endpoints/reports/reports';
import type { ThumbsDownCheck } from '@/common/api/models/thumbsDownCheck';

import type {
  ThumbsDownCheckSelection,
  ThumbsDownCheckStatus,
  ThumbsDownCheckView,
} from './thumbsDownCheckView';

function formatCheckTime(at: string | undefined | null): string {
  if (!at) return '';
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function mapThumbsDownCheck(data: ThumbsDownCheck | null | undefined): ThumbsDownCheckView | null {
  if (!data) return null;
  return {
    reason: data.reason,
    votedAtLabel: formatCheckTime(data.votedAt),
    checkedAtLabel: formatCheckTime(data.checkedAt),
    draft: data.draft ?? null,
    observation: data.observation ?? null,
    nextStep: data.nextStep ?? null,
  };
}

function isNotFoundError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('response' in error)) return false;
  const status = (error as { response?: { status?: number } }).response?.status;
  return status === 404;
}

export type UseThumbsDownCheckResult = {
  status: ThumbsDownCheckStatus;
  check: ThumbsDownCheckView | null;
  refetch: () => void;
};

export function useThumbsDownCheck(selection: ThumbsDownCheckSelection | null): UseThumbsDownCheckResult {
  const queryClient = useQueryClient();
  const enabled = selection !== null;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAiTutorThumbsDownCheck(
    selection?.moduleId ?? '',
    selection?.sessionId ?? '',
    selection?.voteId ?? '',
    {
      query: {
        enabled,
        staleTime: Infinity,
        retry: false,
        select: (res) => mapThumbsDownCheck(res?.data?.data),
      },
    },
  );

  useEffect(() => {
    if (enabled && isError && isNotFoundError(error)) {
      queryClient.invalidateQueries({ queryKey: getGetAiTutorThumbsDownQueryKey() }).catch(() => undefined);
    }
  }, [enabled, error, isError, queryClient]);

  const status: ThumbsDownCheckStatus = useMemo(() => {
    if (!selection) return 'idle';
    if (isLoading && !data) return 'loading';
    if (isError && isNotFoundError(error)) return 'gone';
    if (isError) return 'error';
    return 'ready';
  }, [data, error, isError, isLoading, selection]);

  return {
    status,
    check: data ?? null,
    refetch,
  };
}
